import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useVerticalsList } from "@/lib/verticalsApi";
import { useSiteSettings } from "@/hooks/useSiteSettings";

function _origin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export default function VerticalsIndex() {
  const { data, loading, error } = useVerticalsList();
  const { data: ss } = useSiteSettings();
  const verticals = data?.docs || [];
  const siteName = ss?.displayName || ss?.companyName || "MTS";
  const title = `Industry Verticals — ${siteName}`;
  const description =
    `Explore MTS export verticals: furniture, building materials, natural stone, timber, prefabricated houses and more — each with dedicated research and sourcing guides.`;
  const canonical = `${_origin()}/verticals`;

  return (
    <main className="max-w-6xl mx-auto px-4 py-12" data-testid="verticals-index-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <nav className="text-xs text-neutral-500 mb-6" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2 text-neutral-300">/</span>
        <span className="text-neutral-900">Verticals</span>
      </nav>

      <header className="mb-10">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-900">
          Industry Verticals
        </h1>
        <p className="mt-4 text-neutral-600 max-w-2xl">
          MTS operates as an execution partner across export verticals from
          Indonesia and wider Asia. Each vertical carries its own master research,
          supplier network and content library — pick a vertical to explore.
        </p>
      </header>

      {loading && (
        <div className="text-neutral-500" data-testid="verticals-loading">Loading verticals…</div>
      )}
      {error && (
        <div className="text-red-600" data-testid="verticals-error">
          Unable to load verticals: {error}
        </div>
      )}

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {verticals.filter((v) => (v.articleCount ?? 0) > 0).map((v) => (
          <li key={v.slug}>
            <Link
              to={`/verticals/${v.slug}`}
              className="block h-full border border-neutral-200 hover:border-neutral-900 transition p-6 rounded-sm"
              data-testid={`vertical-card-${v.slug}`}
            >
              <div className="text-xs uppercase tracking-wider text-neutral-500">
                Vertical
              </div>
              <div className="mt-2 text-xl font-semibold text-neutral-900">
                {v.name}
              </div>
              {v.description && (
                <p className="mt-3 text-sm text-neutral-600 line-clamp-3">
                  {v.description}
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
